import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import ShortUniqueId from 'short-unique-id';
import {
  addCalculationField,
  addResultForIfElse,
  setResultTypeForIfElse,
} from '../../../redux/Calculations/CalculationsActions';
import { isSimilarDataType } from '../../CommonFunctions/CommonFunctions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { useEffect, useRef, useState } from 'react';
import { isFlowFilledProperly, isFlowUsed, isFlowAlreadyPresentInStep } from '../utils';
import { RootState } from '../../../redux';
const ResultDropZone = ({ children }: { children: React.ReactNode }) => {
  const [alert, setAlert] = useState<any>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const textMessage = useRef<string[]>([]);
  const [showBorder, setShowBorder] = useState(false);

  const canDrop = (): boolean => selectedFlow?.condition !== 'Else';
  const dispatch = useDispatch();
  const { propKey, calculations } = useSelector((state: RootState) => ({
    propKey:
      state.tabTileProps.selectedTabId.toString() +
      '.' +
      state.tabTileProps.selectedTileId.toString(),
    calculations: state.calculations,
  }));

  const currentCalculationSession = calculations.properties[propKey]?.currentCalculationSession;

  const [{ isOver }, dropResult] = useDrop({
    accept: ['card', 'step', 'calculation', 'calculationSelected'],
    canDrop: canDrop,
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop(item: any) {
      setShowBorder(false);
      if (
        !currentCalculationSession ||
        !currentCalculationSession?.calculationInfo ||
        currentCalculationSession.activeCondition === null ||
        !currentCalculationSession.activeFlow
      )
        return;

      const calculationInfo = currentCalculationSession?.calculationInfo;
      if (
        isFlowUsed(
          Object.keys(calculationInfo.flows),
          calculationInfo.flows,
          activeFlow,
          calculationInfo,
        )
      ) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
      }
      if (['calculation', 'step'].includes(item.type)) {
        setAlert({
          severity: 'warning',
          message: 'Using another calculated field as a source feature is coming in future release',
        });
        return;
      }
      if (item.type !== 'calculationSelected' && !item.fieldData) return;

      if (isDropNotAllowed(item.uid, activeFlow)) {
        console.log('Drop not allowed: Same flow');
        return;
      }

      if (item.type === 'calculationSelected') {
        const currentCalculationAggregation =
          currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow!][0]
            ?.isAggregation;
        const droppedFlowAggregation =
          currentCalculationSession?.calculationInfo?.flows[item.uid][0]?.isAggregation;

        if (
          item.type === 'calculationSelected' &&
          currentCalculationAggregation !== droppedFlowAggregation
        ) {
          setAlert({
            severity: 'warning',
            message: 'Can not drop a flow with different aggregation',
          });
          return;
        }
      }

      if (!selectedResultType || onlyConditionWithResultField) {
        dispatch(
          setResultTypeForIfElse(
            propKey,
            currentCalculationSession.activeFlow!,
            item.type !== 'calculationSelected'
              ? item.fieldData.dataType
              : currentCalculationSession.calculationInfo.flows[item.uid][0]?.flow === 'IfElse'
                ? currentCalculationSession.calculationInfo.selectedResultType[item.uid]
                : currentCalculationSession.calculationInfo.flows[item.uid][conditionNumber]
                    .resultantDatatype,
          ),
        );
        if (item.type === 'card') {
          const uId = uid();
          if (!uId) return;

          dispatch(
            addCalculationField(propKey, uId, {
              tableId: item.fieldData.tableId,
              fieldName: item.fieldData.fieldname,
              displayName: item.fieldData.displayname,
              dataType: item.fieldData.dataType,
            }),
          );
          dispatch(
            addResultForIfElse(
              propKey,
              currentCalculationSession.activeFlow,
              conditionNumber,
              uId,
              'field',
            ),
          );
        } else if (item.type === 'calculationSelected') {
          dispatch(
            addResultForIfElse(
              propKey,
              currentCalculationSession.activeFlow,
              conditionNumber,
              item.uid,
              'flow',
            ),
          );
        }
      } else {
        if (!safeToDrop(item.fieldData.dataType)) {
          textMessage.current.push(
            `Data type should be consistent on every result step of a condition. Please add column or static value of same data type on all results in a condition.`,
          );
          textMessage.current.push(
            'Please select a field of similar datatype or change the selected datatype by deleting all the results for all conditions',
          );
          setOpenAlert(true);
          return;
        }
        if (item.type === 'card') {
          const uId = uid();
          if (!uId) return;

          dispatch(
            addCalculationField(propKey, uId, {
              tableId: item.fieldData.tableId,
              fieldName: item.fieldData.fieldname,
              displayName: item.fieldData.displayname,
              dataType: item.fieldData.dataType,
            }),
          );
          dispatch(
            addResultForIfElse(
              propKey,
              currentCalculationSession.activeFlow,
              conditionNumber,
              uId,
              'field',
            ),
          );
        } else if (item.type === 'calculationSelected') {
          dispatch(
            addResultForIfElse(
              propKey,
              currentCalculationSession.activeFlow,
              conditionNumber,
              item.uid,
              'flow',
            ),
          );
        }
      }
    },
  });

  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  if (
    !currentCalculationSession ||
    !currentCalculationSession.activeFlow ||
    currentCalculationSession.activeCondition === null
  )
    return null;
  const uid = new ShortUniqueId({ length: 4 });
  const conditionNumber = currentCalculationSession.activeCondition;
  // introduce step
  const selectedFlow =
    currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow][
      conditionNumber
    ];
  const selectedResultType =
    currentCalculationSession.calculationInfo.selectedResultType[
      currentCalculationSession.activeFlow
    ];
  /**
   * excluding current calculation condition from the list of conditions
   */
  const onlyConditionWithResultField =
    currentCalculationSession.calculationInfo.flows[currentCalculationSession?.activeFlow].filter(
      (condition) => condition?.source.length === 1 && condition?.flowId !== selectedFlow?.flowId,
    ).length === 0;

  const safeToDrop = (type: string): boolean => {
    if (!selectedResultType) return true;
    if (['integer', 'decimal'].includes(selectedResultType)) {
      if (selectedResultType === type.toLowerCase()) return true;
      else return false;
    }
    return isSimilarDataType(selectedResultType, type);
  };

  const activeFlow = calculations.properties[propKey]?.currentCalculationSession?.activeFlow!;
  const isDropNotAllowed = (draggedFlowName: string, activeFlowName: string): boolean => {
    return draggedFlowName === activeFlowName;
  };

  return (
    <>
      <div
        ref={dropResult}
        style={{
          cursor: 'pointer',
          margin: '0',
          textAlign: 'left',
          paddingBottom: '10px',
          paddingTop: '5px',
          minHeight: '4.5rem',
          // backgroundColor:'red',
          // paddingInline: "0.5rem",
          paddingLeft: '0.5rem',
          paddingRight: '0.35rem',
          border: '1px solid transparent',
        }}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
      >
        {children}
      </div>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}

      <NotificationDialog
        severity={'warning'}
        openAlert={openAlert}
        onCloseAlert={() => {
          textMessage.current = [];
          setOpenAlert(false);
        }}
        testMessage={textMessage.current[0]}
      />
      {/* <NotificationDialogV2
        severity="warning"
        textMessages={textMessage.current}
        openAlert={openAlert}
        onCloseAlert={() => {

        }}
      /> */}
    </>
  );
};

export default ResultDropZone;
